import { AlertTriangle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import useTitle from "@/lib/useTitle";
import brand from "@/config/brand";

const DraftBanner = () => (
  <div
    data-testid="legal-draft-banner"
    className="flex items-start gap-3 rounded-xl border border-champagne bg-champagne/20 px-5 py-4 mb-10"
  >
    <AlertTriangle size={18} className="text-turquoise-deep shrink-0 mt-0.5" />
    <p className="font-sans text-sm text-charcoal/80 leading-relaxed">
      <strong>DRAFT — pending review.</strong> This page contains template content prepared for
      review before launch. It is not finalized legal advice and should be reviewed by a qualified
      professional before the website goes live.
    </p>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-serif font-medium text-xl text-charcoal mb-3">{title}</h2>
    <div className="font-sans text-sm text-taupe leading-relaxed space-y-3">{children}</div>
  </section>
);

const Privacy = () => {
  useTitle(
    "Privacy Policy (Draft)",
    "How The Web Whisperer collects, uses, and protects the information you share through this website. Draft pending review."
  );
  return (
    <main data-testid="privacy-page" className="bg-cream pt-[72px]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Reveal>
          <DraftBanner />
          <h1 className="font-serif font-medium tracking-tight text-charcoal text-3xl sm:text-4xl mb-2">
            Privacy Policy
          </h1>
          <p className="font-sans text-xs text-taupe mb-10">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

          <Section title="Information We Collect">
            <p>
              When you submit an inquiry through {brand.name}, we collect the information you
              provide: your name, business name, email address, phone number (optional), project
              details, and any current website address you share with us.
            </p>
          </Section>
          <Section title="How We Use Your Information">
            <p>
              We use the information you provide solely to respond to your inquiry, prepare a
              personalized quote, and deliver the services you request. We do not use your
              information for unrelated marketing without your consent.
            </p>
          </Section>
          <Section title="Sharing of Information">
            <p>
              We do not sell, rent, or trade your personal information. Information may be processed
              by service providers that help us operate (for example, email delivery and website
              hosting providers), only as needed to provide our services.
            </p>
          </Section>
          <Section title="Data Security">
            <p>
              We take reasonable measures to protect the information you share with us. However, no
              method of transmission or storage is completely secure, and we cannot guarantee
              absolute security.
            </p>
          </Section>
          <Section title="Your Choices">
            <p>
              You may contact us at any time to review, correct, or request deletion of the personal
              information you have provided.
            </p>
          </Section>
          <Section title="Changes to This Policy">
            <p>We may update this policy from time to time. The current version will always be posted on this page.</p>
          </Section>
          <Section title="Contact">
            <p>
              Questions about this policy? Email us at{" "}
              <a href={`mailto:${brand.contact.email}`} className="text-turquoise-deep hover:underline">
                {brand.contact.email}
              </a>
              .
            </p>
          </Section>
        </Reveal>
      </div>
    </main>
  );
};

const Terms = () => {
  useTitle(
    "Terms of Service (Draft)",
    "Terms of service for The Web Whisperer's website design, hosting, and care services. Draft pending review."
  );
  return (
    <main data-testid="terms-page" className="bg-cream pt-[72px]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Reveal>
          <DraftBanner />
          <h1 className="font-serif font-medium tracking-tight text-charcoal text-3xl sm:text-4xl mb-2">
            Terms of Service
          </h1>
          <p className="font-sans text-xs text-taupe mb-10">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

          <Section title="Our Services">
            <p>
              {brand.name} provides website design, redesign, custom functionality, hosting, and
              ongoing website care services for small businesses. Every project is quoted
              individually based on its size, complexity, and functionality.
            </p>
          </Section>
          <Section title="Quotes and Proposals">
            <p>
              All projects begin with a personalized proposal that clearly outlines the one-time
              project cost and the ongoing monthly fee before you commit. You are never obligated to
              proceed by requesting a quote.
            </p>
          </Section>
          <Section title="Hosting and Care Plan">
            <p>
              Websites we build and host require an ongoing monthly managed hosting and care plan.
              The plan covers hosting, basic technical maintenance, SSL support, and limited minor
              content updates. It does not include unlimited updates, major redesigns, or new feature
              development, which are quoted separately.
            </p>
          </Section>
          <Section title="Client Responsibilities">
            <p>
              Clients are responsible for providing accurate business information, timely feedback
              during the design process, and any content (text, photos, logos) they wish to use,
              along with confirmation they have the right to use it.
            </p>
          </Section>
          <Section title="No Guaranteed Outcomes">
            <p>
              We design professional, search-engine-friendly websites, but we do not guarantee search
              engine rankings, website traffic, leads, or sales results.
            </p>
          </Section>
          <Section title="Portfolio Display">
            <p>
              Unless you ask us not to, we may showcase completed work in our portfolio as an example
              of our services.
            </p>
          </Section>
          <Section title="Changes to These Terms">
            <p>We may update these terms from time to time. The current version will always be posted on this page.</p>
          </Section>
          <Section title="Contact">
            <p>
              Questions about these terms? Email us at{" "}
              <a href={`mailto:${brand.contact.email}`} className="text-turquoise-deep hover:underline">
                {brand.contact.email}
              </a>
              .
            </p>
          </Section>
        </Reveal>
      </div>
    </main>
  );
};

export { Privacy, Terms };
